/**
 * Avisos do chat: o sino (lista montada no servidor, que esconde o que já não se pode ler) e os avisos do browser
 * (ADR-0017, fatia 3). Sem e-mail (revisão #9). Tudo pelo cliente tipado; os avisos novos chegam pelo tempo real.
 */

import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-typed";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";
import type { Schemas } from "@/types/api";
import { CHAT_ROOT } from "./people-chat-keys";

export type ChatNotification = Schemas["ChatNotificationRead"];
type NotificationList = Schemas["ChatNotificationList"];

export const bellKey = [...CHAT_ROOT, "notifications"] as const;
const settingsKey = [...CHAT_ROOT, "settings"] as const;

export function useChatBell(enabled: boolean) {
  return useQuery({ queryKey: bellKey, queryFn: () => api.get("/chat/notifications", { query: { limit: 30 } }), enabled, staleTime: 60_000 });
}

export function useMarkChatNotificationsRead() {
  const qc = useQueryClient();
  return useMutationWithToast<NotificationList, Error, { ids?: string[]; conversation_id?: string | null }>({
    mutationFn: (body) => api.put("/chat/notifications/read", { body: { ids: body.ids ?? [], conversation_id: body.conversation_id ?? null } }),
    onSuccessCallback: (list) => {
      qc.setQueryData(bellKey, list);
    },
  });
}

export function useChatSettings(enabled = true) {
  return useQuery({ queryKey: settingsKey, queryFn: () => api.get("/chat/settings"), enabled, staleTime: 5 * 60_000 });
}

export function useUpdateChatSettings() {
  const qc = useQueryClient();
  return useMutationWithToast<Schemas["ChatUserSettingsRead"], Error, Schemas["ChatUserSettingsUpdate"]>({
    mutationFn: (body) => api.put("/chat/settings", { body }),
    onSuccessCallback: (saved) => {
      qc.setQueryData(settingsKey, saved);
    },
    errorMessage: "Não foi possível guardar as preferências.",
  });
}

/** Um aviso que chegou pelo tempo real: entra no topo do sino e, se não for silencioso, aparece no browser. */
export function receiveNotification(qc: QueryClient, notification: ChatNotification, silent: boolean, viewingConversationId: string | null): void {
  qc.setQueryData<NotificationList>(bellKey, (list) => {
    if (!list) return list;
    if (list.items.some((n) => n.id === notification.id)) return list;
    return { items: [notification, ...list.items].slice(0, 30), unread_count: list.unread_count + 1 };
  });
  const focusedHere = typeof document !== "undefined" && document.visibilityState === "visible" && viewingConversationId === notification.conversation_id;
  if (silent || focusedHere || typeof window === "undefined" || !("Notification" in window) || window.Notification.permission !== "granted") return;
  const title = notification.kind === "direta" ? (notification.author_name ?? "Mensagem nova") : `${notification.author_name ?? "Alguém"} · ${notification.conversation_title}`;
  try {
    const alert = new window.Notification(title, { body: notification.snippet, tag: `chat:${notification.conversation_id}` });
    alert.onclick = () => {
      window.focus();
      window.location.assign(`/dashboard/chat?c=${encodeURIComponent(notification.conversation_id)}`);
    };
  } catch {
    // alguns browsers só deixam avisos a partir de um service worker: o sino continua a ter o aviso
  }
}

export function canAskBrowserPermission(): boolean {
  return typeof window !== "undefined" && "Notification" in window && window.Notification.permission === "default";
}
